import { Hash, MusicNotes } from '@phosphor-icons/react';
import {
  Badge,
  Checkbox,
  cn,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@reelplicate/ui';

import {
  captionCount,
  formatAgo,
  formatDuration,
  formatViews,
  STATUS,
  typeLabel,
} from './library';
import type { Asset, Collection } from './types';

// Figma "Table" (191:2480): rows are 16px in from the card edge; the ui
// table's default cell padding is replaced to match.
const head = 'h-auto py-2.5 px-0';
const cell = 'h-auto py-2 px-0 text-body-sm text-text-primary';

type MediaTableProps = {
  assets: Asset[];
  collections: Collection[];
  selected: Set<string>;
  onSelectedChange: (ids: Set<string>) => void;
  onOpen: (asset: Asset) => void;
};

function sourceText(asset: Asset) {
  if (asset.source === 'saved') return `Saved · ${asset.savedFrom}`;
  return asset.source === 'generated' ? 'Generated' : 'Uploaded';
}

function lengthText(asset: Asset) {
  if (asset.kind === 'caption')
    return `${captionCount(asset.caption ?? '').split(' ')[0]} chars`;
  return asset.duration !== undefined ? formatDuration(asset.duration) : '—';
}

/** Figma "Screen — Media · List + upload" (191:2149): the library as rows. */
export function MediaTable({
  assets,
  collections,
  selected,
  onSelectedChange,
  onOpen,
}: MediaTableProps) {
  const all = assets.length > 0 && assets.every((a) => selected.has(a.id));
  const some = assets.some((a) => selected.has(a.id));
  const toggle = (id: string, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(id);
    else next.delete(id);
    onSelectedChange(next);
  };

  return (
    <div className="bg-bg-surface border-border-subtle w-full overflow-hidden rounded-lg border">
      <Table fixed aria-label="Media" className="min-w-[1000px]">
        <TableHeader>
          <TableRow>
            <TableHead width={44} className={cn(head, 'pl-4')}>
              <Checkbox
                checked={all ? true : some ? 'indeterminate' : false}
                onCheckedChange={(on) =>
                  onSelectedChange(
                    on === true ? new Set(assets.map((a) => a.id)) : new Set(),
                  )
                }
                aria-label="Select all"
              />
            </TableHead>
            <TableHead className={head}>Name</TableHead>
            <TableHead width={84} className={head}>
              Type
            </TableHead>
            <TableHead width={140} className={head}>
              Source
            </TableHead>
            <TableHead width={110} className={head}>
              Status
            </TableHead>
            <TableHead width={64} className={head}>
              Length
            </TableHead>
            <TableHead width={72} align="right" className={cn(head, 'pr-3')}>
              Views
            </TableHead>
            <TableHead width={76} className={cn(head, 'pl-3')}>
              Added
            </TableHead>
            <TableHead width={156} className={cn(head, 'pr-4')}>
              Collections
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {assets.map((asset) => {
            const inCollection = collections.find((c) => c.assetIds?.includes(asset.id));
            return (
              <TableRow key={asset.id} selected={selected.has(asset.id)}>
                <TableCell className={cn(cell, 'pl-4')}>
                  <Checkbox
                    checked={selected.has(asset.id)}
                    onCheckedChange={(on) => toggle(asset.id, on === true)}
                    aria-label={`Select ${asset.title}`}
                  />
                </TableCell>
                <TableCell className={cell}>
                  <button
                    type="button"
                    onClick={() => onOpen(asset)}
                    className="focus-visible:shadow-focus-ring flex min-w-0 items-center gap-2 rounded-xs text-left outline-none"
                  >
                    <span className="bg-bg-subtle flex h-12 w-7 shrink-0 items-center justify-center overflow-hidden rounded-[5px]">
                      {asset.thumbnail ? (
                        <img
                          src={asset.thumbnail}
                          alt=""
                          className="size-full object-cover"
                        />
                      ) : (
                        (() => {
                          const Placeholder = asset.kind === 'audio' ? MusicNotes : Hash;
                          return (
                            <Placeholder
                              aria-hidden
                              className="text-icon-tertiary size-3.5"
                            />
                          );
                        })()
                      )}
                    </span>
                    <span className="text-body-sm-strong truncate">{asset.title}</span>
                  </button>
                </TableCell>
                <TableCell className={cell}>{typeLabel(asset)}</TableCell>
                <TableCell className={cell}>{sourceText(asset)}</TableCell>
                <TableCell className={cell}>
                  {asset.status ? (
                    <Badge size="sm" dot tone={STATUS[asset.status].tone}>
                      {STATUS[asset.status].label}
                    </Badge>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell className={cell}>{lengthText(asset)}</TableCell>
                <TableCell className={cn(cell, 'pr-3 text-right')}>
                  {asset.views !== undefined ? formatViews(asset.views) : '—'}
                </TableCell>
                <TableCell className={cn(cell, 'text-text-secondary pl-3 capitalize')}>
                  {formatAgo(asset.addedDaysAgo)}
                </TableCell>
                <TableCell className={cn(cell, 'pr-4')}>
                  {inCollection ? <Tag>{inCollection.name}</Tag> : '—'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
